import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import i18n from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));

  const getAuthHeaders = useCallback(() => {
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  }, [token]);

  // Sync i18n with user.preferred_language whenever user changes
  useEffect(() => {
    const lang = user?.preferred_language;
    if (lang && ['fr', 'nl', 'en'].includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [user]);

  const checkAuth = useCallback(async () => {
    // Capture whether a token existed BEFORE the request so a spurious 401 on
    // initial mount (no token to send) never wipes fresh state written by
    // another flow in parallel (e.g. AuthVerifyEmailPage.verifyEmail()).
    const hadToken = !!localStorage.getItem('auth_token');
    if (!hadToken) {
      // No token to verify → nothing to fetch. Skip the /auth/me round-trip
      // entirely and let downstream flows populate state as needed.
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setUser(res.data);
    } catch {
      // Only wipe if the token we DID send was rejected. If, in the meantime,
      // another flow already stored a new one in localStorage (verifyEmail
      // race), leave it alone.
      const current = localStorage.getItem('auth_token');
      if (current) {
        setUser(null);
        setToken(null);
        localStorage.removeItem('auth_token');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = res.data;
    setToken(newToken);
    localStorage.setItem('auth_token', newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, phone) => {
    const res = await axios.post(`${API}/auth/register`, {
      email, password, name, phone,
      preferred_language: (i18n.language || 'fr').slice(0, 2).toLowerCase(),
    });
    const { token: newToken, user: userData, email_verified } = res.data;
    // Auto-login only when the backend already marks the account as verified
    // (currently: only the seeded admin). Regular users must confirm their
    // email inbox first — the caller decides how to communicate that.
    if (newToken && userData && email_verified) {
      setToken(newToken);
      localStorage.setItem('auth_token', newToken);
      setUser(userData);
      return { user: userData, needsVerification: false };
    }
    return { user: null, needsVerification: true, email: res.data.email || email };
  };

  const verifyEmail = async (verifyToken) => {
    const res = await axios.post(`${API}/auth/verify-email`, { token: verifyToken });
    const { token: newToken, user: userData } = res.data;
    setToken(newToken);
    localStorage.setItem('auth_token', newToken);
    setUser(userData);
    return res.data;
  };

  const resendVerification = async (email) => {
    await axios.post(`${API}/auth/resend-verification`, { email });
  };

  const updateLanguage = useCallback(async (lang) => {
    // Update immediately in i18n + localStorage for instant UX
    i18n.changeLanguage(lang);
    // Persist to backend if logged in
    if (token) {
      try {
        await axios.put(`${API}/auth/language`, { preferred_language: lang }, {
          headers: getAuthHeaders(),
          withCredentials: true,
        });
        setUser(u => u ? { ...u, preferred_language: lang } : u);
      } catch { /* non-blocking */ }
    }
  }, [token, getAuthHeaders]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
    } catch { /* ignore */ }
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
  };

  const loginWithGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + '/auth/google';
    try {
      const res = await axios.get(`${API}/auth/google/login`, {
        params: { redirect_uri: redirectUri },
      });
      // Store state for CSRF check on return (optional but recommended)
      try { sessionStorage.setItem('google_oauth_state', res.data.state); } catch {}
      window.location.href = res.data.url;
    } catch (e) {
      console.error('Could not start Google login', e);
      alert("Connexion Google indisponible. Réessayez plus tard.");
    }
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, setToken, loading, token, login, register, logout,
      loginWithGoogle, getAuthHeaders, checkAuth, updateLanguage,
      verifyEmail, resendVerification,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
